import { useEffect, useRef, useState } from "react";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { isAndroid } from "@scrile/tools/dist/lib/browserUtils";
import useAuthUser from "hooks/useAuthUser";
import useKeepScrollPosition from "hooks/useKeepScrollPosition";
import emitter, { EVENTS } from "lib/emitter";
import useController from "./controller";

export default function useViewController(userId: string, setSenderUser: (senderUser: UserPublicData | null) => void) {
  const [showUnread, setShowUnread] = useState(true);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { shouldScrollToBottom } = useKeepScrollPosition(contentRef);
  const { userRef } = useAuthUser();

  // Forwarded into MessagesListView; the controller's hydrated→fresh phase
  // transition fires this ref to pin the viewport to bottom across the
  // cache→fresh data swap (eliminates the up-down "shake" after page reload).
  const settleAtBottomFnRef = useRef<(() => void) | null>(null);

  const {
    thread,
    messages,
    senderUser,
    loadMore,
    scrollMessages,
    loadingMessages,
    setObserve,
    minUnreadMessageId,
    updateMessage,
    deleteMessage,
    loadUntilMessage,
  } = useController(userId, shouldScrollToBottom, settleAtBottomFnRef);

  // Virtuoso's firstItemIndex keeps scroll anchored on prepend now — the old
  // manual keep-scroll (which fought Virtuoso) is gone. Pass through to loadMore.
  const wrappedLoadMore = (...args: any[]) => loadMore(...args);

  const onFocus = () => {
    if (isAndroid()) {
      shouldScrollToBottom.current = true;
    }
  };

  const scrollToBottomFnRef = useRef<(() => void) | null>(null);

  const isNearBottom = () => {
    const el = contentRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };

  const scrollToBottom = () => {
    if (scrollToBottomFnRef.current) {
      scrollToBottomFnRef.current();
      return;
    }
    const el = contentRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    const onNewMessage = async (message: Message | undefined) => {
      if (!message || !thread || thread.id !== message.threadId) {
        return;
      }
      // Own message — always scroll to bottom after Virtuoso processes the update
      if (message.senderId === userRef.current?.id) {
        setTimeout(() => scrollToBottom(), 0);
        setShowUnread(false);
        return;
      }
      // Incoming message near bottom — scroll after Virtuoso processes
      if (isNearBottom()) {
        setTimeout(() => scrollToBottom(), 0);
      }
    };
    emitter.on<Message>(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
    return () => {
      emitter.off<Message>(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, userRef]);

  const [cachedUnreadMessageID, setCachedUnreadMessageID] = useState(0);
  useEffect(() => {
    const minUnreadId = (minUnreadMessageId && Number(minUnreadMessageId)) || 0;
    setCachedUnreadMessageID((prev) => {
      if (minUnreadId > 0 && prev === 0) return minUnreadId;
      if (minUnreadId === 0 && prev !== 0) return 0;
      return prev;
    });
  }, [minUnreadMessageId]);

  useEffect(() => {
    setSenderUser(senderUser);

    return () => {
      setSenderUser(null);
      setCachedUnreadMessageID(0);
      setShowUnread(true);
    };
  }, [senderUser, setSenderUser]);

  return {
    showUnread,
    messages,
    senderUser,
    loadMore: wrappedLoadMore,
    scrollMessages,
    loadingMessages,
    setObserve,
    onFocus,
    minUnreadMessageId: cachedUnreadMessageID.toString(),
    reverseContentRef: contentRef,
    scrollToBottomFnRef,
    settleAtBottomFnRef,
    thread,
    updateMessage,
    deleteMessage,
    loadUntilMessage,
  };
}
